import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useBasket } from '../../../../bff-api/baskets/useBasket';
import { usePaymentMethods } from '../../../../bff-api/baskets/usePaymentMethods';
import { getFourGiftCardMessages } from '../../../../bff-api/utils/basket-utils';
import { AddToBagButton } from '../../../../components/add-to-bag-section/add-to-bag-button/AddToBagButton';
import { AfterpaySection } from '../../../../components/afterpay-section/AfterpaySection';
import { ApplePayButton } from '../../../../components/apple-pay-button';
import { Field } from '../../../../components/field';
import { messages, useGiftCardFields } from '../../../../components/forms/useGiftCardFields';
import { GiftCardMessageInput } from '../../../../components/gift-card-message-input/GiftCardMessageInput';
import { NotificationBanner } from '../../../../components/notification-banner/NotificationBanner';
import { usePDP } from '../../../../contexts/pdp/usePDP';
import { Button, Header, QuantityInput, Text } from '../../../../ui-components';
import { MAX_QUANTITY_INPUT_NUMBER } from '../../../../ui-components/inputs/quantity-input/const';
import { Toggle } from '../../../../ui-components/inputs/toggle/Toggle';
import { useCustomPreferenceOrDefault } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { CHAR_LIMIT_PER_LINE, MAX_LINES } from '../../../checkout/model/GiftOptions';
import { useCheckApplePayEligibility } from '../../hooks/useCheckApplePayEligibility';
import { DesignSelector } from './DesignSelector';
import { GiftCardAmount } from './GiftCardAmount';
import type { Product } from '../../../../bff-api/products/model';

export interface GiftCardBuyBoxForm {
    giftCardAmount: string;
    giftCardSenderName: string;
    giftCardRecipientEmail: string;
    giftCardRecipientName: string;
    giftCardMessage: string;
}

type GiftCardBuyBoxProps = {
    onExploreFAQsClick: () => void;
    onCheckBalanceClick: () => void;
};

export const GiftCardBuyBox = ({
    onExploreFAQsClick,
    onCheckBalanceClick,
}: GiftCardBuyBoxProps): JSX.Element | null => {
    const { data: basket } = useBasket();

    const hasInitializedGiftCardAmount = useRef<boolean>(false);

    const {
        product,
        selectedQuantity,
        setSelectedQuantity: setQuantity,
        giftCardAmount: selectedGiftCardAmount,
        setGiftCardAmount,
    } = usePDP();

    const intl = useIntl();

    const [sendToMe, setSendToMe] = useState<boolean>(false);
    const physicalCardDeliveryMessage = useCustomPreferenceOrDefault(
        CustomPreference.PHYSICAL_GIFT_CARD_DELIVERY_NOTIFICATION,
        '',
    );
    const electronicCardDeliveryMessage = useCustomPreferenceOrDefault(
        CustomPreference.ELECTRONIC_GIFT_CARD_DELIVERY_NOTIFICATION,
        '',
    );
    const paymentMethods = usePaymentMethods({ productId: product?.id }).data;
    const form = useForm<GiftCardBuyBoxForm>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
    });

    const fields = useGiftCardFields(form, undefined, sendToMe);
    const {
        watch,
        reset,
        formState: { errors },
    } = form;
    const giftCardAmount = watch('giftCardAmount');
    const giftCardMessageValue = watch('giftCardMessage');
    const giftCardSenderName = watch('giftCardSenderName');
    const giftCardRecipientEmail = watch('giftCardRecipientEmail');
    const giftCardRecipientName = watch('giftCardRecipientName');
    const giftCardRecipientEmailValue = watch('giftCardRecipientEmail');
    const giftCardRecipientNameValue = watch('giftCardRecipientName');
    const giftCardSenderNameValue = watch('giftCardSenderName');

    useEffect(() => {
        if (giftCardAmount) {
            setGiftCardAmount(giftCardAmount);
        }
    }, [giftCardAmount, setGiftCardAmount]);

    useEffect(() => {
        if (hasInitializedGiftCardAmount.current || !selectedGiftCardAmount) {
            return;
        }

        const currentAmount = form.getValues('giftCardAmount');

        if (!currentAmount) {
            form.setValue('giftCardAmount', selectedGiftCardAmount, {
                shouldDirty: false,
                shouldTouch: true,
                shouldValidate: true,
            });
            hasInitializedGiftCardAmount.current = true;
        }
    }, [form, selectedGiftCardAmount]);

    const { isApplePayVisible, params } = useCheckApplePayEligibility({
        customAmount: Number(giftCardAmount) || Number(selectedGiftCardAmount),
        giftMessage: giftCardMessageValue,
        senderName: giftCardSenderName,
        recipientName: giftCardRecipientName,
        recipientEmail: giftCardRecipientEmail,
    });

    const resetFormFields = (fieldsToReset: Record<string, string>): void => {
        const currentValues = form.getValues();

        reset({
            ...currentValues,
            ...fieldsToReset,
        });
    };

    const isPhysicalGiftCard = product?.c_gcType === 'physical';

    const handleAddToBag = useCallback(async () => {
        const isFormValid = await form.trigger();
        if (!isFormValid) {
            return false;
        }

        return true;
    }, [form]);

    const customProduct = useMemo<Product>(() => {
        const additionalFields = isPhysicalGiftCard
            ? {
                  c_gcAmount: Number(selectedGiftCardAmount) || Number(giftCardAmount),
              }
            : {
                  c_gcRecipientEmail: giftCardRecipientEmailValue,
                  c_gcRecipientName: giftCardRecipientNameValue,
                  c_gcSenderName: giftCardSenderNameValue,
                  c_gcAmount: Number(selectedGiftCardAmount) || Number(giftCardAmount),
                  c_gcDeliveryDate: dayjs().format('MM/DD/YYYY'),
              };

        return {
            ...product!,
            ...getFourGiftCardMessages(giftCardMessageValue),
            ...additionalFields,
        };
    }, [
        isPhysicalGiftCard,
        selectedGiftCardAmount,
        giftCardAmount,
        giftCardRecipientEmailValue,
        giftCardRecipientNameValue,
        giftCardSenderNameValue,
        product,
        giftCardMessageValue,
    ]);

    const deliveryMessage = isPhysicalGiftCard
        ? physicalCardDeliveryMessage
        : electronicCardDeliveryMessage;

    return (
        <Flex
            direction="column"
            gap={6}
            backgroundColor="white"
            paddingX={{ base: 4, sm: 8, md: 0 }}
            data-testid="gift-card-buy-box"
        >
            <Header variant="header3" as="h3" color="slt-pepper.1000">
                {isPhysicalGiftCard ? (
                    <FormattedMessage
                        defaultMessage="Physical Gift Card"
                        id="gift_card.details_page.title-physical"
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="E-Gift Card"
                        id="gift_card.details_page.title-electronic"
                    />
                )}
            </Header>
            <DesignSelector product={product} />
            <GiftCardAmount
                giftCardAmount={giftCardAmount}
                giftCardAmountField={fields.giftCardAmount}
                onResetFormFields={resetFormFields}
            />
            {!isPhysicalGiftCard && (
                <>
                    <Flex>
                        <Toggle
                            id="send-to-me-toggle"
                            checked={sendToMe}
                            onChange={() => setSendToMe(!sendToMe)}
                            label={intl.formatMessage(messages.sendToMeToggleLabel)}
                            description={intl.formatMessage(messages.sendToMeToggleDescription)}
                            variant="new-toggle"
                        />
                    </Flex>
                    <Flex direction="column" gap={6} data-testid="gift-card-buy-box-form">
                        <Flex
                            direction={{ base: 'column', sm: 'row', md: 'column', lg: 'row' }}
                            gap={{ base: 6, sm: 4, md: 6, lg: 4 }}
                        >
                            <Field {...fields.giftCardRecipientName} />
                            <Field {...fields.giftCardRecipientEmail} />
                        </Flex>
                        <Field inputProps={{ width: '100%' }} {...fields.giftCardSenderName} />
                    </Flex>
                </>
            )}
            {isPhysicalGiftCard && (
                <QuantityInput
                    id={`gift-card-item-quantity-${product.id}`}
                    onChange={setQuantity}
                    maxValue={MAX_QUANTITY_INPUT_NUMBER}
                    value={selectedQuantity}
                    onBlur={setQuantity}
                    strictMaxValue={false}
                />
            )}
            <Flex direction="column" gap={1} data-testid="gift-card-message-container">
                <GiftCardMessageInput
                    name="giftCardMessage"
                    maxPerLine={CHAR_LIMIT_PER_LINE}
                    maxLines={MAX_LINES}
                    control={form.control}
                    setValue={form.setValue}
                    watch={form.watch}
                    clearErrors={form.clearErrors}
                    setError={form.setError}
                />
                <Flex justifyContent="space-between">
                    <Flex direction="column" gap={1}>
                        {errors.giftCardMessage ? (
                            <Text size="small" color="slt-red-error">
                                {errors.giftCardMessage.message}
                            </Text>
                        ) : (
                            giftCardMessageValue?.replace(/\n/g, '').length >=
                                CHAR_LIMIT_PER_LINE * MAX_LINES && (
                                <Text size="small">
                                    <FormattedMessage
                                        id="gift_card_message_max_length"
                                        defaultMessage="Your message reached the allowed character limit."
                                    />
                                </Text>
                            )
                        )}
                    </Flex>
                    <Text size="small">
                        {giftCardMessageValue ? giftCardMessageValue.replace(/\n/g, '').length : 0}/
                        {CHAR_LIMIT_PER_LINE * MAX_LINES}
                    </Text>
                </Flex>
            </Flex>
            {deliveryMessage ? (
                <NotificationBanner id="gift-card-delivery-message">
                    <Text>{deliveryMessage}</Text>
                </NotificationBanner>
            ) : null}
            {isApplePayVisible && (
                <ApplePayButton
                    step="newgdp"
                    basket={basket}
                    product={product}
                    params={params}
                    validationFn={handleAddToBag}
                />
            )}
            <AddToBagButton
                customProduct={customProduct}
                ignoreInventory
                onAddToBag={handleAddToBag}
            />
            {paymentMethods?.afterPay.enabled && (
                <AfterpaySection
                    product={product}
                    afterpayConfig={paymentMethods.afterPay.config}
                    eligible={paymentMethods.afterPay.eligible}
                    mode="messaging"
                />
            )}
            <Flex gap={4}>
                <Button
                    variant="tertiary"
                    padding="0"
                    onClick={onExploreFAQsClick}
                    id="gift-card-details-page-explore-faqs"
                >
                    <FormattedMessage
                        defaultMessage="Explore our FAQs"
                        id="gift-card-details-page.explore-faqs"
                    />
                </Button>
                <Button
                    variant="tertiary"
                    padding="0"
                    onClick={onCheckBalanceClick}
                    id="gift-card-details-page-check-balance"
                >
                    <FormattedMessage
                        defaultMessage="Gift Card Balance"
                        id="gift-card-details-page.gift-card-balance"
                    />
                </Button>
            </Flex>
        </Flex>
    );
};
